import {
  Autocomplete as MuiAutocomplete,
  Box,
  Checkbox,
  Grid,
  MenuItem,
  Typography,
  FormControlLabel,
} from '@mui/material';
import { get } from 'lodash-es';
import states from '../../../lib/usStates';
import { useContext } from './context';
import { TextField } from '../../../components/inputs/TextField';
import ErrorMessage from '../../../components/ErrorMessage';
import { useFeatureFlag } from '../../../hooks/use-feature-flag';

export function Form() {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <Error />
      <Fields />
    </Box>
  );
}

function Header() {
  const { isNew } = useContext();
  return (
    <>
      <Typography variant="h4">
        {isNew ? 'New Operation' : 'Edit Operation'}
      </Typography>
      <Typography variant="caption">
        {`${isNew ? 'Add' : 'Edit'} Operation Information using the text fields below`}
      </Typography>
    </>
  );
}

function Error() {
  const { error } = useContext();
  if (!error) return null;
  return <ErrorMessage sx={{ mt: 2, mx: { md: 2 } }} error={error} />;
}

function Fields() {
  return (
    <Grid container sx={{ mt: 2, px: { md: 2 } }}>
      <RequiredFields xs={12} md={4} sx={{ pr: { md: 5 } }} />
      <AddressFields
        xs={12}
        md={8}
        sx={{
          mt: { xs: 3, md: 0 },
          pl: { md: 5 },
          borderLeft: { md: '1px solid rgba(0, 0, 0, 0.12)' },
        }}
      />
    </Grid>
  );
}

function RequiredFields(props) {
  const isZohoContractsEnabled = useFeatureFlag('ZohoContractPermissions');
  const {
    operation,
    organizations,
    isNew,
    updateOperation,
    isAdmin,
    isOrganizationActive,
  } = useContext();

  return (
    <Grid item {...props}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Required Fields
      </Typography>

      <Grid container spacing={{ xs: 3, sm: 5 }}>
        <Grid container item direction="column" xs={12} sm={9} spacing={3}>
          <Input
            tabIndex={1}
            disabled={!isNew && operation.organization.isDefault}
            label="Operation Name"
            name="operationName"
            helperText={
              operation.organization.isDefault && !isNew
                ? 'Name changes should be made in Farmgate'
                : ''
            }
          />
          <Grid
            item
            sx={{
              width: '100%',
              '& .MuiAutocomplete-tag': {
                maxWidth: 'calc(100% - 45px)',
              },
            }}
          >
            <MuiAutocomplete
              options={organizations}
              fullWidth
              disableCloseOnSelect={false}
              disableClearable
              name="organizationId"
              data-testid="organizations"
              value={
                organizations.find(
                  (org) => org.id === operation.organizationId,
                ) || null
              }
              onChange={(e, value) =>
                updateOperation({ name: 'organizationId', value: value.id })
              }
              getOptionKey={(option) => option.id}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              renderOption={({ key, ...optionProps }, option) => (
                <MenuItem key={key} {...optionProps}>
                  {option.name}
                </MenuItem>
              )}
              renderInput={(params) => (
                <TextField label="Organization" {...params} />
              )}
            />
          </Grid>
          {isAdmin && (
            <Input tabIndex={2} label="Slack Webhook" name="slackWebHook" />
          )}

          {isAdmin && isZohoContractsEnabled && (
            <Grid item sx={{ width: '100%' }}>
              <TextField
                disabled
                fullWidth
                label="Contracts"
                name="contractTypes"
                value={operation.contractTypes?.join(', ')}
                multiline
                minRows={2}
              />
            </Grid>
          )}

          <Grid item tabIndex={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!operation.isActive}
                  disabled={!isOrganizationActive && !isNew}
                  onChange={(e) =>
                    updateOperation({
                      name: 'isActive',
                      value: e.target.checked,
                    })
                  }
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

function AddressFields(props) {
  return (
    <Grid item {...props}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Address Fields
      </Typography>

      <Grid container spacing={{ xs: 3, sm: 5 }}>
        <Grid container item direction="column" xs={12} sm={4} spacing={3}>
          <Input
            tabIndex={21}
            label="Office Phone"
            name="officePhone"
            mask="+{1} (#00) 000-0000"
          />
          <Input tabIndex={22} label="Address Line 1" name="addressLine1" />
          <Input tabIndex={23} label="Address Line 2" name="addressLine2" />
        </Grid>
        <Grid container item direction="column" xs={12} sm={4} spacing={3}>
          <Input tabIndex={25} label="City" name="city" />
          <Input tabIndex={26} label="Zip" name="zip" />
          <Select
            tabIndex={27}
            label="State"
            name="state"
            options={states.map(mapState)}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}

function mapState(state) {
  return (
    <MenuItem key={state.abbreviation} value={state.abbreviation}>
      {state.name}
    </MenuItem>
  );
}

function Select({ options, ...props }) {
  return (
    <Input {...props} select>
      {options}
    </Input>
  );
}

function Input({ name, ...passThrough }) {
  const { operation, updateOperation, error } = useContext();

  return (
    <Grid item sx={{ width: '100%' }}>
      <TextField
        fullWidth
        name={name}
        value={get(operation, name) || ''}
        onChange={(e) => updateOperation(e.target)}
        error={get(error?.validationErrors, name)?.message}
        {...passThrough}
      />
    </Grid>
  );
}
